import React, { useState, useEffect, useRef, useCallback } from "react";
import { Phone, PhoneOff, PhoneIncoming, Mic, MicOff, Loader2 } from "lucide-react";
import { getSocket } from "../../utils/socket";
import { SOCKET_EVENTS } from "../../constants/liveChatSocket";
import {
  initiateCall,
  acceptCall,
  rejectCall,
  terminateCall,
  requestCallPermission,
  createPermissionTemplate,
} from "../../apis/calls";
import { toast } from "react-toastify";
import "./CallPanel.css";

const ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];
const ICE_GATHER_TIMEOUT_MS = 2500;

/**
 * CallPanel — WhatsApp Business Calling (voice) over browser-direct WebRTC.
 *
 * Signaling goes through the wabaserver (AiSensy Call Actions + webhooks);
 * audio flows directly between this browser and Meta's media servers.
 *
 * Outbound: parent sets `startCallRequest` = { phoneId, to, name, ts }.
 * Inbound: listens for `incoming_call` socket events globally.
 */
const CallPanel = ({ startCallRequest }) => {
  // status: idle | preparing | dialing | ringing | active | ended
  const [call, setCall] = useState(null);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const timerRef = useRef(null);
  const callRef = useRef(null);
  callRef.current = call;

  /* ---------- WebRTC helpers ---------- */

  const cleanupMedia = useCallback(() => {
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (_) {}
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setMuted(false);
    setDuration(0);
  }, []);

  const endCallUI = useCallback((message) => {
    cleanupMedia();
    setCall(null);
    if (message) toast.info(message);
  }, [cleanupMedia]);

  const createPeerConnection = useCallback((stream) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));
    pc.ontrack = (event) => {
      if (remoteAudioRef.current && event.streams?.[0]) {
        remoteAudioRef.current.srcObject = event.streams[0];
        remoteAudioRef.current.play().catch(() => {});
      }
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "closed"].includes(pc.connectionState)) {
        const current = callRef.current;
        if (current?.callId && current.status === "active") {
          terminateCall(current.phoneId, current.callId).catch(() => {});
          endCallUI("Call connection lost");
        }
      }
    };
    pcRef.current = pc;
    return pc;
  }, [endCallUI]);

  const waitForIceGathering = (pc) =>
    new Promise((resolve) => {
      if (pc.iceGatheringState === "complete") return resolve();
      const done = () => {
        pc.removeEventListener("icegatheringstatechange", check);
        resolve();
      };
      const check = () => {
        if (pc.iceGatheringState === "complete") done();
      };
      pc.addEventListener("icegatheringstatechange", check);
      setTimeout(done, ICE_GATHER_TIMEOUT_MS);
    });

  const getMic = async () => {
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      toast.error("Microphone access is required for calls. Allow mic permission and retry.");
      throw err;
    }
  };

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
  };

  /* ---------- Outbound ---------- */

  const startOutboundCall = useCallback(async ({ phoneId, to, name }) => {
    if (callRef.current) {
      toast.warn("Already in a call");
      return;
    }
    setCall({ direction: "outbound", status: "preparing", phoneId, phone: to, name });
    try {
      const stream = await getMic();
      localStreamRef.current = stream;
      const pc = createPeerConnection(stream);
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      await waitForIceGathering(pc);

      const res = await initiateCall(phoneId, to, pc.localDescription.sdp);
      const callId = res.data?.data?.callId;
      if (!callId) throw new Error("No call id returned");
      setCall((c) => (c ? { ...c, status: "dialing", callId } : c));
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.message || err.message || "Failed to start call";
      endCallUI(null);

      // No call permission yet → offer to send a permission request
      if (err.response?.status === 403 && data?.permission) {
        const send = window.confirm(
          `${name || to} hasn't allowed calls from your business yet.\n\nSend them a call permission request on WhatsApp? (They must tap Allow before you can call.)`
        );
        if (send) {
          try {
            const permRes = await requestCallPermission(phoneId, to);
            const channel = permRes.data?.data?.channel;
            toast.success(
              channel === "template"
                ? "Call permission template sent. You can call once they allow it."
                : "Call permission request sent. You can call once they allow it."
            );
          } catch (permErr) {
            const permData = permErr.response?.data;
            // Window closed + no approved CPR template → offer to create one
            if (permData?.code === "no_cpr_template") {
              const create = window.confirm(
                "The 24-hour chat window is closed, so a call-permission TEMPLATE is required — and you don't have one yet.\n\nCreate one now? (Meta must approve it before it can be sent.)"
              );
              if (create) {
                try {
                  await createPermissionTemplate(phoneId);
                  toast.success(
                    "Call permission template submitted for approval. Once approved, click Call again to send it."
                  );
                } catch (createErr) {
                  toast.error(
                    createErr.response?.data?.message || "Failed to create the template"
                  );
                }
              }
            } else if (permData?.code === "cpr_template_pending") {
              toast.info(permData.message);
            } else {
              toast.error(permData?.message || "Failed to send permission request");
            }
          }
        }
        return;
      }
      toast.error(msg);
    }
  }, [createPeerConnection, endCallUI]);

  /* ---------- Inbound ---------- */

  const answerIncomingCall = useCallback(async () => {
    const current = callRef.current;
    if (!current || current.status !== "ringing" || !current.sdpOffer) return;
    try {
      setCall((c) => ({ ...c, status: "preparing" }));
      const stream = await getMic();
      localStreamRef.current = stream;
      const pc = createPeerConnection(stream);
      await pc.setRemoteDescription({ type: "offer", sdp: current.sdpOffer });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await waitForIceGathering(pc);

      await acceptCall(current.phoneId, current.callId, pc.localDescription.sdp);
      setCall((c) => (c ? { ...c, status: "active" } : c));
      startTimer();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to answer call";
      endCallUI(null);
      toast.error(msg);
    }
  }, [createPeerConnection, endCallUI]);

  const declineIncomingCall = useCallback(async () => {
    const current = callRef.current;
    if (!current?.callId) return;
    try {
      await rejectCall(current.phoneId, current.callId);
    } catch (_) {}
    endCallUI(null);
  }, [endCallUI]);

  /* ---------- Shared ---------- */

  const hangUp = useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    if (current.callId) {
      try {
        await terminateCall(current.phoneId, current.callId);
      } catch (_) {}
    }
    endCallUI(null);
  }, [endCallUI]);

  const toggleMute = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  };

  /* ---------- Socket wiring ---------- */

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;

    const onIncoming = (data) => {
      if (callRef.current) return; // busy — let it ring elsewhere / miss
      setCall({
        direction: "inbound",
        status: "ringing",
        callId: data.call_id,
        phoneId: data.phone_number_id,
        phone: data.contact_phone,
        name: data.contact_phone,
        sdpOffer: data.sdp_offer,
      });
      if (typeof Notification !== "undefined" && Notification.permission === "granted" && document.hidden) {
        try { new Notification("Incoming WhatsApp call", { body: data.contact_phone }); } catch (_) {}
      }
    };

    const onAnswered = async (data) => {
      const current = callRef.current;
      if (!current || current.callId !== data.call_id) return;
      const pc = pcRef.current;
      if (!pc || !data.sdp_answer) return;
      try {
        await pc.setRemoteDescription({ type: "answer", sdp: data.sdp_answer });
        setCall((c) => (c ? { ...c, status: "active" } : c));
        startTimer();
      } catch (err) {
        console.error("Failed to apply SDP answer:", err);
        hangUp();
      }
    };

    const onEnded = (data) => {
      const current = callRef.current;
      if (!current || (data.call_id && current.callId && data.call_id !== current.callId)) return;
      endCallUI(
        data.outcome === "completed"
          ? "Call ended"
          : data.outcome
            ? `Call ${data.outcome}`
            : "Call ended"
      );
    };

    socket.on(SOCKET_EVENTS.INCOMING_CALL, onIncoming);
    socket.on(SOCKET_EVENTS.CALL_ANSWERED, onAnswered);
    socket.on(SOCKET_EVENTS.CALL_ENDED, onEnded);
    return () => {
      socket.off(SOCKET_EVENTS.INCOMING_CALL, onIncoming);
      socket.off(SOCKET_EVENTS.CALL_ANSWERED, onAnswered);
      socket.off(SOCKET_EVENTS.CALL_ENDED, onEnded);
    };
  }, [endCallUI, hangUp]);

  /* ---------- Outbound trigger from parent ---------- */

  useEffect(() => {
    if (startCallRequest?.ts && startCallRequest.phoneId && startCallRequest.to) {
      startOutboundCall(startCallRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startCallRequest?.ts]);

  // Cleanup on unmount
  useEffect(() => () => cleanupMedia(), [cleanupMedia]);

  /* ---------- UI ---------- */

  const fmtDuration = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <>
      {/* Hidden element that plays the remote audio stream */}
      <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: "none" }} />

      {call && (
        <div className={`callpanel ${call.status === "ringing" ? "callpanel--ringing" : ""}`}>
          <div className="callpanel-header">
            {call.direction === "inbound" ? <PhoneIncoming size={15} /> : <Phone size={15} />}
            <span className="callpanel-title">
              {call.status === "ringing" && "Incoming call"}
              {call.status === "preparing" && "Connecting…"}
              {call.status === "dialing" && "Calling…"}
              {call.status === "active" && "On call"}
            </span>
            {call.status === "active" && (
              <span className="callpanel-timer">{fmtDuration(duration)}</span>
            )}
          </div>

          <div className="callpanel-peer">
            <div className="callpanel-avatar">
              {(call.name || call.phone || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="callpanel-peer-info">
              <span className="callpanel-name">{call.name || call.phone}</span>
              <span className="callpanel-phone">{call.phone}</span>
            </div>
          </div>

          <div className="callpanel-actions">
            {call.status === "ringing" && (
              <>
                <button className="callpanel-btn callpanel-btn--accept" onClick={answerIncomingCall} title="Answer">
                  <Phone size={17} />
                </button>
                <button className="callpanel-btn callpanel-btn--reject" onClick={declineIncomingCall} title="Decline">
                  <PhoneOff size={17} />
                </button>
              </>
            )}

            {(call.status === "dialing" || call.status === "preparing") && (
              <>
                <Loader2 size={17} className="callpanel-spin" />
                <button className="callpanel-btn callpanel-btn--reject" onClick={hangUp} title="Cancel">
                  <PhoneOff size={17} />
                </button>
              </>
            )}

            {call.status === "active" && (
              <>
                <button
                  className={`callpanel-btn ${muted ? "callpanel-btn--muted" : "callpanel-btn--neutral"}`}
                  onClick={toggleMute}
                  title={muted ? "Unmute" : "Mute"}
                >
                  {muted ? <MicOff size={17} /> : <Mic size={17} />}
                </button>
                <button className="callpanel-btn callpanel-btn--reject" onClick={hangUp} title="Hang up">
                  <PhoneOff size={17} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CallPanel;
